import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { FintechAddComponent } from '../fintech-add/fintech-add.component';
import { FintechService } from '../../../../services/fintech.service';
import { HttpClient } from '@angular/common/http';
import { FormBuilder } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, of, switchMap, Subscription, EMPTY, Subject } from 'rxjs';
import { retry, catchError, takeUntil } from 'rxjs/operators';
import dropin from 'braintree-web-drop-in';
import { ToastrService } from 'ngx-toastr';

// Add the necessary types for Braintree dropin if they are not globally available
declare var dropin: any;

@Component({
    selector: 'app-fintech-add-braintree',
    templateUrl: './fintech-add-braintree.component.html',
    styleUrls: ['./fintech-add-braintree.component.scss']
})
export class FintechAddBraintreeComponent extends FintechAddComponent implements OnInit, OnDestroy {
    braintreeToken;
    dropinInstance;
    private destroy$ = new Subject<void>();

    constructor(fintechService: FintechService,
                http: HttpClient,
                formBuilder: FormBuilder,
                changeDetectorRef: ChangeDetectorRef,
                private dialogRef: MatDialogRef<FintechAddBraintreeComponent>,
                private toastr: ToastrService) {
        super(fintechService, http, formBuilder, changeDetectorRef);
    }

    ngOnInit(): void {
        const obs$ = this.fintechService.getPayerToken().pipe(
            takeUntil(this.destroy$),
            switchMap(payerToken => {
                console.log("payerToken=" + payerToken);
                const url = "https://pay.signage.me:8443/braintree/CreateBraintreeToken";
                const options = {token: payerToken};
                // Apply retry logic to the first POST call
                return this.http.post(url, options).pipe(
                    retry({
                        count: 5,
                        delay: (error, retryCount) => {
                            this.toastr.info(`Please braintree connecting to server... (${retryCount})`, `FinTech`, { timeOut: 3000 });
                            return of(error);
                        },
                    }),
                    catchError(error => {
                        console.error('Error in CreateBraintreeToken POST call after retries:', error);
                        this.toastr.error('Failed to connect to the server. Please try again later.', 'FinTech', { timeOut: 5000 });
                        return of(null);
                    })
                );
            }),
            switchMap(bt => {
                if (!bt) {
                    return EMPTY; // Stop the observable chain on error
                }
                this.braintreeToken = (<any>bt).braintreeToken;
                console.log("braintreeToken=" + this.braintreeToken);
                dropin.create({
                    authorization: this.braintreeToken,
                    container: '#braintree-container',
                    card: {
                        cardholderName: true
                    }
                }, (err, dropinInstance) => {
                    if (err) {
                        console.error("Error creating dropin instance:", err);
                        this.toastr.error('Failed to load payment form. Please try again.', 'FinTech');
                    }
                    this.dropinInstance = dropinInstance;
                });
                return of('a');
            })
        );

        obs$.subscribe();
    }

    onBillingCancel() {
        this.dialogRef.close(null);
    }

    onBillingSubmit() {
        this.payMethodInfo.address1 = this.billForm.value.address1;
        this.payMethodInfo.address2 = this.billForm.value.address2;
        this.payMethodInfo.city = this.billForm.value.city;
        this.payMethodInfo.state = this.billForm.value.state;
        this.payMethodInfo.postalCode = this.billForm.value.postalCode;
        this.payMethodInfo.country = this.billForm.value.country;

        this.messageColor = "white";
        this.billMessage = "Please wait updating....";

        let payerToken;
        const obs$ = this.fintechService.getPayerToken().pipe(
            takeUntil(this.destroy$),
            switchMap(pt => {
                payerToken = pt;
                return new Observable<any>(sub => {
                    this.dropinInstance.requestPaymentMethod().then((payload) => {
                        if (payload && payload.nonce) {
                            this.fintechService.setBusy(true);
                            sub.next(payload);
                            sub.complete();
                        } else {
                            console.error('Braintree payload or nonce is missing.');
                            this.toastr.error('Payment method request failed. Please try again.', 'FinTech');
                            sub.error('Payment method request failed.');
                        }
                    }).catch(err => {
                        console.error('Braintree requestPaymentMethod error:', err);
                        this.toastr.error('An error occurred with the payment form. Please try again.', 'FinTech');
                        sub.error(err);
                    });
                });
            }),
            switchMap(payload => {
                const billAddress = {
                    address1: this.payMethodInfo.address1,
                    address2: this.payMethodInfo.address2,
                    city: this.payMethodInfo.city,
                    state: this.payMethodInfo.state,
                    postalCode: this.payMethodInfo.postalCode,
                    country: this.payMethodInfo.country
                };

                const url = "https://pay.signage.me:8443/braintree/createPaymentMethod";
                const options = {
                    token: payerToken,
                    PaymentMethodNonce: payload.nonce,
                    billingAddress: billAddress
                };
                // Apply retry logic to the second POST call
                return this.http.post(url, options).pipe(
                    retry({
                        count: 5,
                        delay: (error, retryCount) => {
                            this.toastr.info(`Please wait connecting to server... (${retryCount})`, `FinTech`, { timeOut: 3000 });
                            return of(error);
                        },
                    }),
                    catchError(error => {
                        console.error('Error in createPaymentMethod POST call after retries:', error);
                        this.toastr.error('Failed to create payment method on the server.', 'FinTech', { timeOut: 5000 });
                        this.fintechService.setBusy(false);
                        return EMPTY; // Return EMPTY to stop the observable chain
                    })
                );
            }),
            switchMap(ret => {
                const data: any = ret;
                this.payMethodInfo.payMethodId = data.payMethodId;
                this.payMethodInfo.cardholder = data.cardholder;
                this.payMethodInfo.last4 = data.last4;
                this.payMethodInfo.expiration = data.expiration;
                this.payMethodInfo.address1 = data.address1;
                this.payMethodInfo.address2 = data.address2;
                this.payMethodInfo.city = data.city;
                this.payMethodInfo.state = data.state;
                this.payMethodInfo.postalCode = data.postalCode;
                this.payMethodInfo.country = data.country;
                this.messageColor = "white";
                this.billMessage = "";
                this.dialogRef.close(this.payMethodInfo);
                this.fintechService.setBusy(false);
                return of('a');
            })
        );
        obs$.subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
